import type { SemanticFormatSpec } from "@/features/data-hub/model/semanticFormat";
import {
  buildFormatFromMeasurementValues,
  createEmptyMeasurementFormValues,
  measurementToFormValues,
  type SemanticMeasurementFormValues,
} from "@/features/data-hub/model/semanticMeasurement";

/** Grain levels the analytical SQL layer understands. */
export const TIME_GRAIN_OPTIONS = ["day", "week", "month", "quarter"] as const;

export type SemanticDimension = {
  key?: string;
  name?: string;
  description?: string;
  expression?: string;
  source_ref?: Record<string, unknown>;
  entity?: string;
  /**
   * Read by the analytical SQL adapter, the SQL agent context pack and the nugget
   * tracking plan to decide what the time axis is. Left false, nothing treats this as
   * a date even when it plainly is one.
   */
  is_time_dimension?: boolean;
  time_grain_support?: string[];
  confidence?: number;
  provenance?: string;
  format?: SemanticFormatSpec;
};

export type SemanticDimensionFormValues = {
  name: string;
  description: string;
  expression: string;
  entity: string;
  isTimeDimension: boolean;
  timeGrainSupport: string[];
  /** Unit formatting only — a dimension has no materiality. */
  measurement: SemanticMeasurementFormValues;
};

export function slugifyDimensionKey(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^\w]+/g, "_")
    .replace(/^_|_$/g, "");
}

export function dimensionToFormValues(
  dimension: SemanticDimension,
): SemanticDimensionFormValues {
  return {
    name: (dimension.name ?? "").trim(),
    description: (dimension.description ?? "").trim(),
    expression: (dimension.expression ?? "").trim(),
    entity: (dimension.entity ?? "").trim(),
    isTimeDimension: dimension.is_time_dimension === true,
    timeGrainSupport: Array.isArray(dimension.time_grain_support)
      ? [...dimension.time_grain_support]
      : [],
    measurement: measurementToFormValues(dimension.format, undefined),
  };
}

export function createEmptyDimensionFormValues(): SemanticDimensionFormValues {
  return {
    name: "",
    description: "",
    expression: "",
    entity: "",
    isTimeDimension: false,
    timeGrainSupport: [],
    measurement: createEmptyMeasurementFormValues(),
  };
}

export function buildDimensionFromFormValues(
  values: SemanticDimensionFormValues,
  existing?: SemanticDimension,
): SemanticDimension {
  const trimmedName = values.name.trim();
  const key = (existing?.key ?? "").trim() || slugifyDimensionKey(trimmedName);
  const expression = values.expression.trim();
  const parts = expression.includes(".") ? expression.split(".") : [];
  const sourceRef: Record<string, unknown> =
    parts.length >= 2
      ? { table: parts.slice(0, -1).join("."), column: parts[parts.length - 1] }
      : expression
        ? { column: expression }
        : {};
  return {
    ...existing,
    key,
    name: trimmedName,
    description: values.description.trim() || undefined,
    expression: expression || undefined,
    source_ref: sourceRef,
    entity: values.entity.trim() || undefined,
    is_time_dimension: values.isTimeDimension,
    time_grain_support: values.timeGrainSupport.filter((grain) => grain.trim()),
    // Explicit rather than via the `...existing` spread, so clearing the unit clears it.
    format: buildFormatFromMeasurementValues(values.measurement),
    confidence: 1,
    provenance: "interview",
  };
}

export function selectSemanticDimensionsFromGeneratedModel(
  generatedModel: Record<string, unknown>,
): SemanticDimension[] {
  const document =
    generatedModel.document !== null && typeof generatedModel.document === "object"
      ? (generatedModel.document as Record<string, unknown>)
      : {};
  const fromDocument = document.dimensions;
  return Array.isArray(fromDocument) ? (fromDocument as SemanticDimension[]) : [];
}

export function isSemanticDimensionKeyTaken(
  dimensions: SemanticDimension[],
  key: string,
  excludeIndex: number | null = null,
): boolean {
  const normalized = key.trim().toLowerCase();
  if (!normalized) return false;
  return dimensions.some((dimension, index) => {
    if (excludeIndex !== null && index === excludeIndex) return false;
    return (dimension.key ?? "").trim().toLowerCase() === normalized;
  });
}
